import React, { useState, useEffect } from 'react';
import './settings.css';

const SettingsPanel = () => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [systemPrompt, setSystemPrompt] = useState(
    "You are a helpful assistant. Always provide clear, concise, and accurate information."
  );
  const [userScenario, setUserScenario] = useState('');

  // Default configuration
  const [config, setConfig] = useState({
    cogSvcRegion: '',
    cogSvcSubKey: '',
    privateEndpointEnabled: false,
    privateEndpoint: '',
    talkingAvatarCharacter: 'max',
    talkingAvatarStyle: 'casual',
    sttLocales: 'en-US',
    ttsVoice: 'en-US-JennyNeural',
    azureOpenAIEndpoint: '',
    azureOpenAIApiKey: '',
    azureOpenAIDeploymentName: 'gpt-4o',
    enableOyd: false,
    azureCogSearchEndpoint: '',
    azureCogSearchApiKey: '',
    azureCogSearchIndexName: ''
  });

  // Fetch configuration from Flask API
  const fetchConfig = async () => {
    try {
      displayNotification('Loading configuration...', 'info');
      const response = await fetch("http://localhost:5000/api/config");
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const apiConfig = await response.json();
      console.log("Config loaded from API:", apiConfig);
      
      // Map API config to component config structure
      const newConfig = {
        cogSvcRegion: apiConfig.azureSpeech.region,
        cogSvcSubKey: apiConfig.azureSpeech.apiKey,
        privateEndpointEnabled: apiConfig.azureSpeech.enablePrivateEndpoint,
        privateEndpoint: apiConfig.azureSpeech.privateEndpoint,
        talkingAvatarCharacter: apiConfig.avatar.character || 'max',
        talkingAvatarStyle: apiConfig.avatar.style || 'casual',
        sttLocales: apiConfig.sttTts.sttLocales || 'en-US',
        ttsVoice: apiConfig.sttTts.ttsVoice || 'en-US-JennyNeural',
        azureOpenAIEndpoint: apiConfig.azureOpenAI.endpoint,
        azureOpenAIApiKey: apiConfig.azureOpenAI.apiKey,
        azureOpenAIDeploymentName: apiConfig.azureOpenAI.deploymentName || 'gpt-4o',
        enableOyd: false, // Default, not in API config
        azureCogSearchEndpoint: '',
        azureCogSearchApiKey: '',
        azureCogSearchIndexName: ''
      };
      
      // Update system prompt if available
      if (apiConfig.azureOpenAI.prompt) {
        setSystemPrompt(apiConfig.azureOpenAI.prompt);
      }
      
      setConfig(newConfig);
      setConfigLoaded(true);
      displayNotification('Configuration loaded successfully', 'success');
      
      // Save to localStorage for persistence
      localStorage.setItem('avatarConfig', JSON.stringify(newConfig));
      localStorage.setItem('userScenario', userScenario);
      
      return true;
    } catch (error) {
      console.error("Failed to load configuration:", error);
      displayNotification(`Failed to load configuration: ${error.message}`, 'error');
      return false;
    }
  };

  // Display notification messages with auto-fade
  const displayNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: '', type: '' });
    }, 3000);
  };

  // Try to reload configuration from API
  const reloadConfig = () => {
    fetchConfig();
  };

  // Save config changes
  const saveConfig = () => {
    localStorage.setItem('avatarConfig', JSON.stringify(config));
    localStorage.setItem('userScenario', userScenario);
    displayNotification('Configuration saved successfully', 'success');
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setConfig(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  // Handle system prompt changes
  const handlePromptChange = (e) => {
    setSystemPrompt(e.target.value);
  };

  // Handle scenario changes
  const handleScenarioChange = (e) => {
    setUserScenario(e.target.value);
  };

  // Load configuration on component mount
  useEffect(() => {
    // First try to load from API
    fetchConfig().then(success => {
      // If API fetch fails, try loading from localStorage as fallback
      if (!success) {
        const savedScenario = localStorage.getItem('userScenario');
        if (savedScenario) {
          setUserScenario(savedScenario);
        }
        
        const savedConfig = localStorage.getItem('avatarConfig');
        if (savedConfig) {
          try {
            const parsedConfig = JSON.parse(savedConfig);
            setConfig(prevConfig => ({ ...prevConfig, ...parsedConfig }));
            setConfigLoaded(true);
          } catch (e) {
            console.error('Failed to parse saved config:', e);
          }
        }
      }
    });
  }, []);

  return (
    <div className="settings-dashboard">
      <header className="dashboard-header">
        <h1>Avatar Chat Settings</h1>
      </header>
      
      {notification.message && (
        <div className={`notification-banner ${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      <div className="control-panel">
        <div className="panel-header">
          <h2>Configuration</h2>
          
          <div className="config-status-bar">
            <span className={`status-indicator ${configLoaded ? 'active' : 'inactive'}`}>
              {configLoaded ? 'Configuration Loaded' : 'Configuration Not Loaded'}
            </span>
            <button 
              onClick={reloadConfig} 
              className="action-button secondary"
            >
              Reload Config
            </button>
          </div>
        </div>
        
        <div className="panel-card">
          <div className="card-header">
            <h3>Avatar Settings</h3>
          </div>
          
          <div className="form-field">
            <label htmlFor="talkingAvatarCharacter">Avatar Character:</label>
            <select
              id="talkingAvatarCharacter"
              name="talkingAvatarCharacter"
              value={config.talkingAvatarCharacter}
              onChange={handleInputChange}
              className="dropdown-select"
            >
              <option value="harry">Harry</option>
              <option value="meg">Meg</option>
              <option value="lori">Lori</option>
              <option value="daniel">Daniel</option>
              <option value="guy">Guy</option>
              <option value="max">Max</option>
              <option value="emily">Emily</option>
            </select>
          </div>
          
          <div className="form-field">
            <label htmlFor="talkingAvatarStyle">Avatar Style:</label>
            <select
              id="talkingAvatarStyle"
              name="talkingAvatarStyle"
              value={config.talkingAvatarStyle}
              onChange={handleInputChange}
              className="dropdown-select"
            >
              <option value="casual">Casual</option>
              <option value="professional-sitting">Professional Sitting</option>
            </select>
          </div>
        </div>
        
        <div className="panel-card">
          <div className="card-header">
            <h3>Azure Speech Settings</h3>
          </div>
          
          <div className="form-field">
            <label htmlFor="cogSvcRegion">Azure Speech Region:</label>
            <input
              type="text"
              id="cogSvcRegion"
              name="cogSvcRegion"
              value={config.cogSvcRegion}
              onChange={handleInputChange}
              className="settings-text-input"
            />
          </div>
          
          <div className="form-field">
            <label htmlFor="cogSvcSubKey">Azure Speech Key:</label>
            <input
              type="password"
              id="cogSvcSubKey"
              name="cogSvcSubKey"
              value={config.cogSvcSubKey}
              onChange={handleInputChange}
              className="settings-text-input"
            />
          </div>
          
          <div className="form-field checkbox-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="privateEndpointEnabled"
                checked={config.privateEndpointEnabled}
                onChange={handleInputChange}
                className="checkbox-input"
              />
              <span className="checkbox-text">Enable Private Endpoint</span>
            </label>
          </div>
          
          {config.privateEndpointEnabled && (
            <div className="form-field">
              <label htmlFor="privateEndpoint">Private Endpoint URL:</label>
              <input
                type="text"
                id="privateEndpoint"
                name="privateEndpoint"
                value={config.privateEndpoint}
                onChange={handleInputChange}
                className="settings-text-input"
              />
            </div>
          )}
          
          <div className="form-field">
            <label htmlFor="sttLocales">Speech Recognition Locale:</label>
            <input
              type="text"
              id="sttLocales"
              name="sttLocales"
              value={config.sttLocales}
              onChange={handleInputChange}
              className="settings-text-input"
            />
          </div>
          
          <div className="form-field">
            <label htmlFor="ttsVoice">Text-to-Speech Voice:</label>
            <input
              type="text"
              id="ttsVoice"
              name="ttsVoice"
              value={config.ttsVoice}
              onChange={handleInputChange}
              className="settings-text-input"
            />
          </div>
        </div>
        
        <div className="panel-card">
          <div className="card-header">
            <h3>Azure OpenAI Settings</h3>
          </div>
          
          <div className="form-field">
            <label htmlFor="azureOpenAIEndpoint">Azure OpenAI Endpoint:</label>
            <input
              type="text"
              id="azureOpenAIEndpoint"
              name="azureOpenAIEndpoint"
              value={config.azureOpenAIEndpoint}
              onChange={handleInputChange}
              className="settings-text-input"
            />
          </div>
          
          <div className="form-field">
            <label htmlFor="azureOpenAIApiKey">Azure OpenAI Key:</label>
            <input
              type="password"
              id="azureOpenAIApiKey"
              name="azureOpenAIApiKey"
              value={config.azureOpenAIApiKey}
              onChange={handleInputChange}
              className="settings-text-input"
            />
          </div>
          
          <div className="form-field">
            <label htmlFor="azureOpenAIDeploymentName">Deployment Name:</label>
            <input
              type="text"
              id="azureOpenAIDeploymentName"
              name="azureOpenAIDeploymentName"
              value={config.azureOpenAIDeploymentName}
              onChange={handleInputChange}
              className="settings-text-input"
            />
          </div>
        </div>
        
        <div className="panel-card">
          <div className="card-header">
            <h3>System Prompt & Scenario</h3>
          </div>
          
          <div className="form-field">
            <label htmlFor="systemPrompt">System Prompt:</label>
            <textarea
              id="systemPrompt"
              value={systemPrompt}
              onChange={handlePromptChange}
              rows={5}
              className="settings-textarea-input"
            />
          </div>
          
          <div className="form-field">
            <label htmlFor="userScenario">User Scenario:</label>
            <textarea
              id="userScenario"
              value={userScenario}
              onChange={handleScenarioChange}
              rows={3}
              className="settings-textarea-input"
            />
          </div>
        </div>
        
        <div className="action-bar">
          <button 
            onClick={saveConfig}
            className="action-button primary"
          >
            Save Configuration
          </button>
          
          <button 
            onClick={() => window.location.href = '/'}
            className="action-button secondary"
          >
            Back to Chat
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;